import React from 'react';
import type { AdminUser } from '../contentConsoleApi';
import ConsolePagination from './ConsolePagination';
import type { ManagedPasswordForm, ManagedUserForm, PasswordForm, SelfForm } from './models';

export type AccountWorkspaceProps = {
  user: AdminUser;
  canManageUsers: boolean;
  users: AdminUser[];
  userKeyword: string;
  totalUsers: number;
  userCurrentPage: number;
  userTotalPages: number;
  selectedUserId: number | null;
  managedUser: ManagedUserForm;
  managedPassword: ManagedPasswordForm;
  selfForm: SelfForm;
  selfPassword: PasswordForm;
  loadingUsers: boolean;
  saving: boolean;
  setSelfForm: React.Dispatch<React.SetStateAction<SelfForm>>;
  setSelfPassword: React.Dispatch<React.SetStateAction<PasswordForm>>;
  setManagedUser: React.Dispatch<React.SetStateAction<ManagedUserForm>>;
  setManagedPassword: React.Dispatch<React.SetStateAction<ManagedPasswordForm>>;
  onUserKeywordChange: (value: string) => void;
  onUserPageChange: (page: number) => void;
  onSaveSelf: () => Promise<void>;
  onChangeOwnPassword: () => Promise<void>;
  onRefreshUsers: () => Promise<void>;
  onCreateUser: () => void;
  onSelectUser: (user: AdminUser) => void;
  onSaveUser: () => Promise<void>;
  onResetUserPassword: () => Promise<void>;
};

const PermissionChip: React.FC<{ enabled: boolean; title: string; description: string }> = ({ enabled, title, description }) => (
  <div className={`content-console-permission-chip${enabled ? ' is-enabled' : ''}`}>
    <strong>{title}</strong>
    <span>{enabled ? '已开通' : '未开通'}</span>
    <small>{description}</small>
  </div>
);

const AccountWorkspace: React.FC<AccountWorkspaceProps> = ({
  user,
  canManageUsers,
  users,
  userKeyword,
  totalUsers,
  userCurrentPage,
  userTotalPages,
  selectedUserId,
  managedUser,
  managedPassword,
  selfForm,
  selfPassword,
  loadingUsers,
  saving,
  setSelfForm,
  setSelfPassword,
  setManagedUser,
  setManagedPassword,
  onUserKeywordChange,
  onUserPageChange,
  onSaveSelf,
  onChangeOwnPassword,
  onRefreshUsers,
  onCreateUser,
  onSelectUser,
  onSaveUser,
  onResetUserPassword,
}) => {
  const [managementView, setManagementView] = React.useState<'list' | 'edit'>('list');
  const canGrantSuperuser = user.is_superuser;
  const isEditingSelf = managedUser.id === user.id;
  const enabledPermissionSummary = managedUser.is_superuser
    ? '全部模块'
    : [
        managedUser.can_manage_news ? '新闻' : '',
        managedUser.can_manage_resources ? '资源' : '',
        managedUser.can_manage_careers ? '招聘' : '',
        managedUser.can_manage_project_cooperation ? '合作' : '',
        managedUser.can_manage_analytics ? '统计' : '',
        managedUser.can_manage_users ? '用户' : '',
      ].filter(Boolean).join(' / ') || '未开通';

  return (
    <div className={`content-console-account-shell${canManageUsers ? '' : ' is-self-only'}`}>
      <section className="content-console-editor content-console-self-management-shell">
        <div className="content-console-panel-head">
          <div>
            <span className="content-console-panel-tag">ACCOUNT</span>
            <h2>我的账号</h2>
            <p>用于维护当前登录账号的资料、密码及权限信息。</p>
          </div>
        </div>

        <div className="content-console-account-stack">
          <section className="content-console-section-card">
            <div className="content-console-section-head">
              <h3>账号资料</h3>
              <p>用户名与显示名称将同步用于后台界面展示及相关操作记录。</p>
            </div>
            <div className="content-console-form-grid is-two-column">
              <label>
                <span>用户名</span>
                <input value={selfForm.username} onChange={(event) => setSelfForm((current) => ({ ...current, username: event.target.value }))} />
              </label>
              <label>
                <span>显示名称</span>
                <input value={selfForm.display_name} onChange={(event) => setSelfForm((current) => ({ ...current, display_name: event.target.value }))} />
              </label>
            </div>
            <div className="content-console-action-row is-spaced-top">
              <button type="button" className="content-console-primary-button" disabled={saving} onClick={() => void onSaveSelf()}>
                保存资料
              </button>
            </div>
          </section>

          <section className="content-console-section-card">
            <div className="content-console-section-head">
              <h3>修改登录密码</h3>
              <p>建议定期更新密码，并避免多个后台账号使用相同凭据。</p>
            </div>
            <div className="content-console-form-grid is-single-column">
              <label>
                <span>当前密码</span>
                <input type="password" autoComplete="off" value={selfPassword.current_password} onChange={(event) => setSelfPassword((current) => ({ ...current, current_password: event.target.value }))} />
              </label>
              <label>
                <span>新密码</span>
                <input type="password" autoComplete="new-password" value={selfPassword.new_password} onChange={(event) => setSelfPassword((current) => ({ ...current, new_password: event.target.value }))} />
              </label>
              <label>
                <span>确认新密码</span>
                <input type="password" autoComplete="new-password" value={selfPassword.confirm_password} onChange={(event) => setSelfPassword((current) => ({ ...current, confirm_password: event.target.value }))} />
              </label>
            </div>
            <div className="content-console-action-row is-spaced-top">
              <button type="button" className="content-console-primary-button" disabled={saving} onClick={() => void onChangeOwnPassword()}>
                更新密码
              </button>
            </div>
          </section>

          <section className="content-console-section-card is-compact">
            <div className="content-console-section-head">
              <h3>当前账号权限</h3>
              <p>显示当前账号已开通的后台权限。</p>
            </div>
            <div className="content-console-permission-grid">
              <PermissionChip enabled={Boolean(user.is_superuser || user.permissions.can_manage_news)} title="新闻管理" description="维护新闻内容与发布状态" />
              <PermissionChip enabled={Boolean(user.is_superuser || user.permissions.can_manage_resources)} title="资源管理" description="维护资源中心内容与服务案例" />
              <PermissionChip enabled={Boolean(user.is_superuser || user.permissions.can_manage_careers)} title="招聘管理" description="维护岗位信息与投递配置" />
              <PermissionChip enabled={Boolean(user.is_superuser || user.permissions.can_manage_project_cooperation)} title="项目合作" description="维护合作页面内容与问卷配置" />
              <PermissionChip enabled={Boolean(user.is_superuser || user.permissions.can_manage_analytics)} title="访问统计" description="查看流量、页面与地区分析数据" />
              <PermissionChip enabled={Boolean(user.is_superuser || user.permissions.can_manage_users)} title="用户管理" description="创建账号并分配后台权限" />
            </div>
          </section>
        </div>
      </section>

      {canManageUsers ? (
        <section className="content-console-editor content-console-user-management-shell">
          <div className="content-console-panel-head">
            <div>
              <span className="content-console-panel-tag">USERS</span>
              <h2>用户与权限管理</h2>
              <p>用于维护后台账号、状态及模块权限配置。</p>
            </div>
            <div className="content-console-toolbar-cluster">
              <div className="content-console-editor-actions">
                <button type="button" className="content-console-secondary-button" onClick={() => void onRefreshUsers()} disabled={loadingUsers}>
                  刷新列表
                </button>
                <button
                  type="button"
                  className="content-console-secondary-button"
                  onClick={() => {
                    onCreateUser();
                    setManagementView('edit');
                  }}
                >
                  新建账号
                </button>
              </div>
              <div className="content-console-editor-actions">
                <button type="button" className="content-console-primary-button" disabled={saving} onClick={() => void onSaveUser()}>
                  {managedUser.id ? '保存用户' : '创建用户'}
                </button>
              </div>
            </div>
          </div>

          <div className="content-console-tabs content-console-management-tabs">
            <button type="button" className={managementView === 'list' ? 'is-active' : ''} onClick={() => setManagementView('list')}>
              账号列表
            </button>
            <button type="button" className={managementView === 'edit' ? 'is-active' : ''} onClick={() => setManagementView('edit')}>
              编辑面板
            </button>
          </div>

          <div className="content-console-user-management-shell-body">
            <section
              className={[
                'content-console-sidebar',
                'is-inner-sidebar',
                'content-console-user-directory',
                managementView === 'edit' ? 'is-mobile-hidden' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="content-console-list-panel-head content-console-user-directory-head">
                <div className="content-console-list-panel-copy">
                  <strong>后台账号列表</strong>
                  <span>可按用户名或显示名称检索，选中后在右侧编辑区维护。</span>
                </div>
                <input className="content-console-search-input" type="search" value={userKeyword} onChange={(event) => onUserKeywordChange(event.target.value)} placeholder="搜索用户名或显示名称" />
                <div className="content-console-list-summary is-vertical-summary">
                  <span>共 {totalUsers} 个账号</span>
                  <span>{selectedUserId ? `当前编辑 #${selectedUserId}` : '当前为新建模式'}</span>
                </div>
              </div>

              <div className="content-console-list content-console-user-directory-list">
                {loadingUsers ? <div className="content-console-empty">账号列表加载中...</div> : null}
                {!loadingUsers && users.length === 0 ? <div className="content-console-empty">当前没有匹配账号，可以直接新建。</div> : null}
                {!loadingUsers ? users.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`content-console-list-item content-console-user-list-item${selectedUserId === item.id ? ' is-active' : ''}`}
                    onClick={() => {
                      onSelectUser(item);
                      setManagementView('edit');
                    }}
                  >
                    <div className="content-console-list-item-top">
                      <strong>{item.display_name || item.username}</strong>
                      <span className={`content-console-status-badge${item.is_active ? ' is-published' : ' is-draft'}`}>
                        {item.is_active ? '启用' : '停用'}
                      </span>
                    </div>
                    <span className="content-console-user-list-login">{item.username}</span>
                    <div className="content-console-user-list-meta">
                      <small>{item.is_superuser ? '超级管理员' : '普通后台账号'}</small>
                      <small>ID: {item.id}</small>
                    </div>
                  </button>
                )) : null}
              </div>

              {!loadingUsers ? <ConsolePagination currentPage={userCurrentPage} totalPages={userTotalPages} onChange={onUserPageChange} /> : null}
            </section>

            <div className={['content-console-user-editor-stage', managementView === 'list' ? 'is-mobile-hidden' : ''].filter(Boolean).join(' ')}>
              <div className="content-console-user-editor-stage-head">
                <div className="content-console-list-panel-copy">
                  <strong>{managedUser.id ? `编辑账号 #${managedUser.id}` : '新建后台账号'}</strong>
                  <span>资料、权限及密码操作统一在此维护，保存后将刷新列表。</span>
                </div>
                <button type="button" className="content-console-secondary-button content-console-mobile-only-action" onClick={() => setManagementView('list')}>
                  返回账号列表
                </button>
              </div>

              <div className="content-console-editor-shell is-account-editor-shell">
                <div className="content-console-editor-main">
                  <section className="content-console-section-card">
                    <div className="content-console-section-head is-inline-head">
                      <h3>基础资料</h3>
                      <p>新建账号时请设置初始密码；已有账号可继续维护名称及登录名。</p>
                    </div>
                    <div className="content-console-form-grid is-two-column">
                      <label>
                        <span>用户名</span>
                        <input value={managedUser.username} onChange={(event) => setManagedUser((current) => ({ ...current, username: event.target.value }))} />
                      </label>
                      <label>
                        <span>显示名称</span>
                        <input value={managedUser.display_name} onChange={(event) => setManagedUser((current) => ({ ...current, display_name: event.target.value }))} />
                      </label>
                    </div>
                    {!managedUser.id ? (
                      <div className="content-console-form-grid is-single-column">
                        <label>
                          <span>初始密码</span>
                          <input type="password" value={managedUser.password} onChange={(event) => setManagedUser((current) => ({ ...current, password: event.target.value }))} />
                        </label>
                      </div>
                    ) : null}
                  </section>

                  <section className="content-console-section-card">
                    <div className="content-console-section-head is-inline-head">
                      <h3>账号状态与权限</h3>
                      <p>普通账号可以按模块单独授权，超级管理员默认拥有全部权限。</p>
                    </div>
                    <div className="content-console-form-grid is-two-column">
                      <label className="content-console-checkbox">
                        <span>账号状态</span>
                        <div className="content-console-checkbox-row">
                          <input type="checkbox" checked={managedUser.is_active} onChange={(event) => setManagedUser((current) => ({ ...current, is_active: event.target.checked }))} />
                          <span>启用账号</span>
                        </div>
                      </label>
                      <label className="content-console-checkbox">
                        <span>角色标记</span>
                        <div className="content-console-checkbox-row">
                          <input
                            type="checkbox"
                            checked={managedUser.is_superuser}
                            disabled={!canGrantSuperuser || isEditingSelf}
                            onChange={(event) =>
                              setManagedUser((current) => ({
                                ...current,
                                is_superuser: event.target.checked,
                                can_manage_news: event.target.checked ? true : current.can_manage_news,
                                can_manage_resources: event.target.checked ? true : current.can_manage_resources,
                                can_manage_careers: event.target.checked ? true : current.can_manage_careers,
                                can_manage_project_cooperation: event.target.checked ? true : current.can_manage_project_cooperation,
                                can_manage_analytics: event.target.checked ? true : current.can_manage_analytics,
                                can_manage_users: event.target.checked ? true : current.can_manage_users,
                              }))
                            }
                          />
                          <span>超级管理员</span>
                        </div>
                      </label>
                    </div>
                    <p className="content-console-inline-help">
                      {!canGrantSuperuser
                        ? '只有超级管理员可以授予或取消超级管理员权限。'
                        : isEditingSelf
                          ? '当前登录账号不能在这里取消自己的超级管理员身份，避免误操作。'
                          : managedUser.is_superuser
                            ? '当前账号已是超级管理员，默认拥有全部模块权限。'
                            : '勾选后会将该账号提升为超级管理员。'}
                    </p>
                    <div className="content-console-check-grid is-stacked">
                      <label className="content-console-inline-check is-card-check">
                        <input type="checkbox" checked={managedUser.can_manage_news} disabled={managedUser.is_superuser} onChange={(event) => setManagedUser((current) => ({ ...current, can_manage_news: event.target.checked }))} />
                        <span>
                          <strong>新闻管理</strong>
                          <small>维护新闻内容</small>
                        </span>
                      </label>
                      <label className="content-console-inline-check is-card-check">
                        <input type="checkbox" checked={managedUser.can_manage_resources} disabled={managedUser.is_superuser} onChange={(event) => setManagedUser((current) => ({ ...current, can_manage_resources: event.target.checked }))} />
                        <span>
                          <strong>资源管理</strong>
                          <small>维护资源中心内容与服务案例</small>
                        </span>
                      </label>
                      <label className="content-console-inline-check is-card-check">
                        <input type="checkbox" checked={managedUser.can_manage_careers} disabled={managedUser.is_superuser} onChange={(event) => setManagedUser((current) => ({ ...current, can_manage_careers: event.target.checked }))} />
                        <span>
                          <strong>招聘管理</strong>
                          <small>维护岗位内容与默认展示配置</small>
                        </span>
                      </label>
                      <label className="content-console-inline-check is-card-check">
                        <input type="checkbox" checked={managedUser.can_manage_project_cooperation} disabled={managedUser.is_superuser} onChange={(event) => setManagedUser((current) => ({ ...current, can_manage_project_cooperation: event.target.checked }))} />
                        <span>
                          <strong>项目合作</strong>
                          <small>维护合作内容与问卷二维码</small>
                        </span>
                      </label>
                      <label className="content-console-inline-check is-card-check">
                        <input type="checkbox" checked={managedUser.can_manage_analytics} disabled={managedUser.is_superuser} onChange={(event) => setManagedUser((current) => ({ ...current, can_manage_analytics: event.target.checked }))} />
                        <span>
                          <strong>访问统计</strong>
                          <small>查看访问量、来源地区和趋势分析</small>
                        </span>
                      </label>
                      <label className="content-console-inline-check is-card-check">
                        <input type="checkbox" checked={managedUser.can_manage_users} disabled={managedUser.is_superuser} onChange={(event) => setManagedUser((current) => ({ ...current, can_manage_users: event.target.checked }))} />
                        <span>
                          <strong>用户管理</strong>
                          <small>添加账号并分配权限</small>
                        </span>
                      </label>
                    </div>
                    <div className="content-console-action-row is-spaced-top">
                      <button type="button" className="content-console-primary-button" disabled={saving} onClick={() => void onSaveUser()}>
                        保存权限设置
                      </button>
                    </div>
                  </section>

                  {managedUser.id && !managedUser.is_superuser ? (
                    <section className="content-console-section-card">
                      <div className="content-console-section-head is-inline-head">
                        <h3>重置用户密码</h3>
                        <p>为当前账号设置新密码。完成重置后，请通知相关人员及时修改密码。</p>
                      </div>
                      <div className="content-console-form-grid is-two-column">
                        <label>
                          <span>新密码</span>
                          <input type="password" value={managedPassword.new_password} onChange={(event) => setManagedPassword((current) => ({ ...current, new_password: event.target.value }))} />
                        </label>
                        <label>
                          <span>确认新密码</span>
                          <input type="password" value={managedPassword.confirm_password} onChange={(event) => setManagedPassword((current) => ({ ...current, confirm_password: event.target.value }))} />
                        </label>
                      </div>
                      <div className="content-console-action-row is-spaced-top">
                        <button type="button" className="content-console-secondary-button" onClick={() => void onResetUserPassword()}>
                          重置此账号密码
                        </button>
                      </div>
                    </section>
                  ) : null}
                </div>

                <aside className="content-console-detail-sidebar">
                  <section className="content-console-section-card is-sidebar-card">
                    <div className="content-console-section-head is-inline-head">
                      <h3>账号概览</h3>
                      <p>用于查看当前账号的状态及权限级别。</p>
                    </div>
                    <dl className="content-console-meta-list">
                      <div>
                        <dt>用户 ID</dt>
                        <dd>{managedUser.id ?? '未创建'}</dd>
                      </div>
                      <div>
                        <dt>账号状态</dt>
                        <dd>{managedUser.is_active ? '已启用' : '已停用'}</dd>
                      </div>
                      <div>
                        <dt>账号类型</dt>
                        <dd>{managedUser.is_superuser ? '超级管理员' : '普通后台账号'}</dd>
                      </div>
                      <div>
                        <dt>已开通权限</dt>
                        <dd>{enabledPermissionSummary}</dd>
                      </div>
                    </dl>
                  </section>

                  <section className="content-console-section-card is-sidebar-card">
                    <div className="content-console-section-head is-inline-head">
                      <h3>模块权限</h3>
                      <p>超级管理员默认拥有全部模块权限。</p>
                    </div>
                    <div className="content-console-permission-grid is-single-column-grid">
                      <PermissionChip enabled={Boolean(managedUser.is_superuser || managedUser.can_manage_news)} title="新闻管理" description="新闻发布与维护" />
                      <PermissionChip enabled={Boolean(managedUser.is_superuser || managedUser.can_manage_resources)} title="资源管理" description="资源中心与服务案例维护" />
                      <PermissionChip enabled={Boolean(managedUser.is_superuser || managedUser.can_manage_careers)} title="招聘管理" description="岗位信息与投递配置" />
                      <PermissionChip enabled={Boolean(managedUser.is_superuser || managedUser.can_manage_project_cooperation)} title="项目合作" description="合作内容与问卷维护" />
                      <PermissionChip enabled={Boolean(managedUser.is_superuser || managedUser.can_manage_analytics)} title="访问统计" description="访问数据与地区分析" />
                      <PermissionChip enabled={Boolean(managedUser.is_superuser || managedUser.can_manage_users)} title="用户管理" description="后台账号维护" />
                    </div>
                  </section>
                </aside>
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default AccountWorkspace;
